import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  CButton,
  CCard,
  CCardBody,
  CCardText,
  CCol,
  CContainer,
  CRow,
  CSpinner,
  CAlert,
} from '@coreui/react'
import { cilArrowCircleLeft, cilWarning } from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import apiClient from '../../services/apiClient'
import { useSwipeable } from 'react-swipeable'
import { addEvent } from '../../utils/calendar'
import { LocalNotifications } from '@capacitor/local-notifications'
import { buildApiUrl } from '../../utils/getConfiguration'
import { SplashScreen } from '@capacitor/splash-screen'
import updateScore from '../../utils/updateScore'
import RecommendationCard, { RecommendationSkeleton } from './components/RecommendationCard'
import InternalAppHeader from './components/InternalAppHeader'
import { useTranslation } from 'react-i18next'
import { normalizeLanguage } from '../../utils/i18nConfig'

const RisksNotification = () => {
  const [loading, setLoading] = useState(false)
  const [recommendations, setRecommendations] = useState([])
  const [query] = useState('')
  const [error, setError] = useState(null)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  useEffect(() => {
    if (typeof window === 'undefined') return
    const storedLang = normalizeLanguage(window.localStorage.getItem('i18nextLng') || i18n.language)
    if (storedLang && storedLang !== i18n.language) {
      i18n.changeLanguage(storedLang)
    }
  }, [i18n])

  const grp_ID = searchParams.get('grp_ID')
  const reminder_ID = searchParams.get('reminder_ID')
  console.log('Risks notification grp_ID ' + grp_ID)
  console.log('Risks notification reminder_ID ' + reminder_ID)
  const handlers = useSwipeable({
    onSwipedLeft: () => console.log('Swiped Left!'),
    onSwipedRight: () => console.log('Swiped Right!'),
    onSwipedUp: () => console.log('Swiped Up!'),
    onSwipedDown: () => console.log('Swiped Down!'),
  })

  const fetchRecommendationsData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const account_ID = localStorage.getItem('account_ID')
      const grp_ID_param = searchParams.get('grp_ID')
      const gender = localStorage.getItem('gender')

      if (!account_ID || !grp_ID_param) {
        console.warn('Missing identifiers for reminder fetch, skipping request.')
        setRecommendations([])
        return
      }

      const response = await apiClient.get(
        buildApiUrl('accountReminders', {
          account_ID,
          grp_ID: grp_ID_param,
          gender: gender || undefined,
        }),
      )
      setRecommendations(response.data)
    } catch (error) {
      console.error('Error fetching recommendations', error)
      setError(t('risksNotification.errors.load', 'Unable to load reminders.'))
    } finally {
      setLoading(false)
    }
  }, [searchParams, t])

  useEffect(() => {
    fetchRecommendationsData()

    let listenerHandle

    const attachListener = async () => {
      try {
        listenerHandle = await LocalNotifications.addListener(
          'localNotificationActionPerformed',
          (notification) => {
            const newReminderID = notification.notification?.extra?.reminder_ID
            if (newReminderID) {
              console.log('New notification received with reminder_ID:', newReminderID)
              fetchRecommendationsData()
            }
          },
        )
      } catch (error) {
        console.error('Unable to attach notification listener', error)
      }
    }

    attachListener()

    return () => {
      listenerHandle?.remove?.()
    }
  }, [fetchRecommendationsData])

  useEffect(() => {
    const splash = document.getElementById('splash-screen')
    if (splash) splash.style.display = 'none'
    if (typeof window === 'undefined') return
    const stored = window.localStorage.getItem('i18nextLng')
    const normalized = normalizeLanguage(stored || i18n.language)
    if (normalized && normalized !== i18n.language) {
      i18n.changeLanguage(normalized)
    }
    window.localStorage.setItem('i18nextLng', normalized)

    SplashScreen.hide({ fadeOutDuration: 200 }).catch((error) => {
      console.warn('Unable to hide splash screen on mount', error)
    })
  }, [i18n])

  useEffect(() => {
    const onLanguageChanged = (lng) => {
      const normalized = normalizeLanguage(lng)
      if (normalized !== lng) {
        i18n.changeLanguage(normalized)
      }
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('i18nextLng', normalized)
      }
    }
    i18n.on('languageChanged', onLanguageChanged)
    return () => {
      i18n.off('languageChanged', onLanguageChanged)
    }
  }, [i18n])

  const filteredRecommendations = useMemo(() => {
    if (!Array.isArray(recommendations) || recommendations.length === 0) {
      return []
    }
    return recommendations.filter((recommendation) =>
      recommendation.Title.toLowerCase().includes(query.toLowerCase()),
    )
  }, [recommendations, query])

  const selectedRecommendation = useMemo(() => {
    if (filteredRecommendations.length === 0) {
      return null
    }

    if (!reminder_ID) {
      return filteredRecommendations[0]
    }

    return (
      filteredRecommendations.find(
        (recommendation) => String(recommendation.reminder_ID) === String(reminder_ID),
      ) || filteredRecommendations[0]
    )
  }, [filteredRecommendations, reminder_ID])

  useEffect(() => {
    SplashScreen.hide({ fadeOutDuration: 200 }).catch((error) => {
      console.warn('Unable to hide splash screen on mount', error)
    })
  }, [])

  const handleClickSwitchOff = async () => {
    try {
      console.log('Account_ID ' + localStorage.getItem('account_ID'))
      console.log('Reminder_ID ' + localStorage.getItem('taskid'))
      const reminderIdStorage = localStorage.getItem('taskid')
      const account_ID = localStorage.getItem('account_ID')
      await apiClient.get(
        buildApiUrl('remindersStatusUpdate', {
          reminder_ID: reminderIdStorage,
          state: 'S',
          account_ID,
        }),
      )
      fetchRecommendationsData()
      console.log('Back')
      window.history.back()
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const handleClickIgnore = async () => {
    try {
      console.log('Account_ID ' + localStorage.getItem('account_ID'))
      console.log('Reminder_ID ' + localStorage.getItem('taskid'))
      const reminderIdStorage = localStorage.getItem('taskid')
      const account_ID = localStorage.getItem('account_ID')
      await apiClient.get(
        buildApiUrl('remindersStatusUpdate', {
          reminder_ID: reminderIdStorage,
          state: 'I',
          account_ID,
        }),
      )
      fetchRecommendationsData()
      console.log('Back')
      window.history.back()
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const handleClickDone = async (points, groupIdParam) => {
    try {
      console.log('Account_ID ' + localStorage.getItem('account_ID'))
      console.log('Reminder_ID ' + localStorage.getItem('taskid'))
      const reminderIdStorage = localStorage.getItem('taskid')
      const account_ID = localStorage.getItem('account_ID')
      await apiClient.get(
        buildApiUrl('remindersStatusUpdate', {
          reminder_ID: reminderIdStorage,
          state: 'D',
          account_ID,
        }),
      )
      await updateScore(points, groupIdParam, fetchRecommendationsData)
      fetchRecommendationsData()
      console.log('Back')
      window.history.back()
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const handleClickPostpone = async () => {
    try {
      const reminderIdStorage = localStorage.getItem('taskid')
      const expecteddate = localStorage.getItem('AImonth')
      const account_ID = localStorage.getItem('account_ID')
      await apiClient.get(
        buildApiUrl('remindersDueDateUpdate', {
          reminder_ID: reminderIdStorage,
          expecteddate,
          account_ID,
        }),
      )
      fetchRecommendationsData()
      console.log('Back')
      window.history.back()
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const selectReminder = (recommendation) => {
    if (!recommendation) return
    localStorage.setItem('taskid', recommendation.reminder_ID)
  }

  const handleDetailNavigation = (recommendation) => {
    if (!recommendation) return
    console.log('Task Detail ' + recommendation.reminder_ID)
    selectReminder(recommendation)
    navigate('/detail')
  }

  const handleSupplierNavigation = (recommendation) => {
    if (!recommendation) return
    console.log('Solve Task ' + recommendation.reminder_ID)
    selectReminder(recommendation)
    localStorage.setItem('supplierid', recommendation.supplier_ID)
    console.log('Supplier ID ' + localStorage.getItem('supplierid'))
    localStorage.setItem('suppliertel', recommendation.supplier_contact_tel)
    localStorage.setItem('suppliername', recommendation.supplier_name)
    console.log('Supplier Name ' + localStorage.getItem('suppliername'))
    localStorage.setItem('grp_ID', grp_ID)
    navigate('/suppliers')
  }

  const handleEshopNavigation = (recommendation) => {
    if (!recommendation) return
    console.log('Task Detail ' + recommendation.reminder_ID)
    selectReminder(recommendation)
    navigate('/eshop')
  }

  const handleCalendarAdd = (recommendation) => {
    if (!recommendation) return
    console.log('Task Detail ' + recommendation.reminder_ID)
    selectReminder(recommendation)
    addEvent(recommendation.Title, recommendation.Description)
  }

  const handleInnovationSuppliers = (recommendation) => {
    if (!recommendation) return
    console.log('Innovation supplier ' + recommendation.supplier_ID)
    selectReminder(recommendation)
    localStorage.setItem('supplierid', recommendation.supplier_ID)
    console.log('Supplier ID ' + localStorage.getItem('supplierid'))
    localStorage.setItem('suppliername', recommendation.supplier_name)
    console.log('Supplier Name ' + localStorage.getItem('suppliername'))
    localStorage.setItem('suppliertel', recommendation.supplier_contact_tel)
    localStorage.setItem('grp_ID', grp_ID)
    navigate('/suppliersinno')
  }

  const handleInnovationStatus = () => {
    console.log('Innovation project status')
    navigate('/suppliersinnoprojectstatus')
  }

  const handleSwitchOffAction = (recommendation) => {
    if (!recommendation) return
    selectReminder(recommendation)
    handleClickSwitchOff()
  }

  const handleIgnoreAction = (recommendation) => {
    if (!recommendation) return
    console.log('Task Ignored')
    selectReminder(recommendation)
    handleClickIgnore()
  }

  const handleDoneAction = (recommendation) => {
    if (!recommendation) return
    console.log('Task Done')
    console.log('Task Detail ' + recommendation.reminder_ID)
    selectReminder(recommendation)
    if (Number(recommendation.expecteddate) === 0) {
      const today = new Date()
      const month = today.getMonth() + 1
      localStorage.setItem('AImonth', Number(month))
      handleClickPostpone()
      console.log('New term for NEW reminder')
    } else {
      if (Number(recommendation.expecteddate) + Number(recommendation.period) > 12) {
        console.log('shift reminder to new year')
        localStorage.setItem('AImonth', Number(recommendation.duedate))
        handleClickPostpone()
        handleClickDone(Number(recommendation.points), Number(recommendation.grp_ID))
      } else {
        console.log('shift reminder within a year')
        localStorage.setItem(
          'AImonth',
          Number(recommendation.expecteddate) + Number(recommendation.period),
        )
        handleClickPostpone()
      }
      console.log('New term for regular reminder')
    }
  }

  useEffect(() => {
    if (!loading) {
      SplashScreen.hide({ fadeOutDuration: 200 }).catch((error) => {
        console.warn('Unable to hide splash screen', error)
      })
    }
  }, [loading])

  const headerTitle = t('risksNotification.header.title')
  const headerSubtitle = selectedRecommendation?.Title || selectedRecommendation?.Description || ''

  const handleNavigateBack = useCallback(() => {
    console.log('Back')
    window.history.back()
  }, [])

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <CSpinner color="primary" variant="grow" style={{ width: '3rem', height: '3rem' }} />
        <div className="text-muted mt-3">
          {t('risksNotification.loading', 'Loading reminders…')}
        </div>
      </div>
    )
  }
  return (
    <div className="risk-notification-wrapper min-vh-100 d-flex flex-column">
      <CContainer>
        <InternalAppHeader title={headerTitle} onBack={handleNavigateBack} />
        {error && (
          <CAlert color="danger" className="mt-3">
            {error}
          </CAlert>
        )}

        <CRow className="justify-content-center g-4 mt-2">
          <CCol lg={10}>
            <CCard className="risk-notification-card border-0 shadow-sm" {...handlers}>
              <CCardBody>
                <div className="risk-notification-card__body">
                  {loading && !selectedRecommendation ? (
                    <RecommendationSkeleton />
                  ) : selectedRecommendation ? (
                    <RecommendationCard
                      recommendation={selectedRecommendation}
                      onDetail={handleDetailNavigation}
                      onSupplier={handleSupplierNavigation}
                      onEshop={handleEshopNavigation}
                      onCalendar={handleCalendarAdd}
                      onSwitchOff={handleSwitchOffAction}
                      onIgnore={handleIgnoreAction}
                      onDone={handleDoneAction}
                      onInnovationSuppliers={handleInnovationSuppliers}
                      onInnovationStatus={handleInnovationStatus}
                    />
                  ) : (
                    <CCardText className="alert alert-info mb-0">
                      {t('risksNotification.empty')}
                    </CCardText>
                  )}
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default RisksNotification
